import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Auth pages
import Login from './pages/Login';

// Student pages
import StudentDashboard from './pages/Student/Dashboard';
import StudentCourseView from './pages/Student/CourseView';
import StudentCourseList from './pages/Student/CourseList';
import StudentQuizzes from './pages/Student/Quizzes';
import StudentQuizAttempt from './pages/Student/QuizAttempt';
import StudentStats from './pages/Student/Stats';
import StudentMessages from './pages/Student/Messages';
import StudentProfile from './pages/Student/Profile';
import StudentMyClass from './pages/Student/MyClass';
import StudentPayments from './pages/Student/Payments';

// Professor pages
import ProfessorDashboard from './pages/Professor/Dashboard';
import ProfessorCourseList from './pages/Professor/CourseList';
import ProfessorCourseView from './pages/Professor/CourseView';
import ProfessorCourseCreator from './pages/Professor/CourseCreator';
import ProfessorCourseAssignment from './pages/Professor/CourseAssignment';
import ProfessorQuizList from './pages/Professor/QuizList';
import ProfessorQuizView from './pages/Professor/QuizView';
import ProfessorQuizCreator from './pages/Professor/QuizCreator';
import ProfessorQuizEdit from './pages/Professor/QuizEdit';
import ProfessorQuizPreview from './pages/Professor/QuizPreview';
import ProfessorMessages from './pages/Professor/MessageList';
import ProfessorProfile from './pages/Professor/Profile';
import ProfessorCourseViewerPage from './pages/Professor/CourseViewerPage';
import ProfessorCourseViewerEdit from './pages/Professor/CourseViewerEdit';

// Admin pages
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminCategory from './pages/Admin/AdminCategories';
import AdminCourses from './pages/Admin/Courses';
import AdminCourseCreator from './pages/Admin/CourseCreator';
import AdminCourseEdit from './pages/Admin/CourseEdit';
import AdminCourseView from './pages/Admin/CourseView';
import AdminListQuiz from './pages/Admin/QuizList'
import AdminQuiz from './pages/Admin/Quizzes';
import AdminQuizCreator from './pages/Admin/QuizCreator';
import AdminQuizView from './pages/Admin/QuizView';
import AdminQuizEdit from './pages/Admin/QuizEdit';
import AdminQuizPreview from './pages/Admin/QuizPreview';
import AdminPayments from './pages/Admin/Payments';
import AdminStats from './pages/Admin/Stats';
import AdminMessages from './pages/Admin/Messages';
import AdminCourseAssignment from './pages/Admin/CourseAssignment';
import AdminCourseModuleEdit from './pages/Admin/CourseModuleEdit';
import AdminCourseModuleViewer from './pages/Admin/CourseModuleViewer';
import QuizReview from './pages/Admin/QuizReview';
import AdminClasses from './pages/Admin/Classes';
import ProfessorClasses from './pages/Admin/Classes';
// Sprint « traçabilité apprenants » (01/10/2026)
import AdminConnections from './pages/Admin/Connections';
import AdminTimeSpent from './pages/Admin/TimeSpent';
// Sprint « émargement » (09/10/2026)
import StudentAttendance from './pages/Student/Attendance';
import ProfessorAttendance from './pages/Professor/Attendance';
import AdminAttendance from './pages/Admin/Attendance';



function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-500"></div>
        <p className="ml-4 text-gray-700">Loading application...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Auth Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            
            {/* Student Routes */}
            <Route path="/student" element={
              <ProtectedRoute allowedRoles={['student']}>
                <Navigate to="/student/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/student/dashboard" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } />
            <Route path="/student/courses" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentCourseList />
              </ProtectedRoute>
            } />
            <Route path="/student/courses/:id" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentCourseView />
              </ProtectedRoute>
            } />
            <Route path="/student/quizzes" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentQuizzes />
              </ProtectedRoute>
            } />
            <Route path="/student/quiz/:quizId/attempt/:attemptId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentQuizAttempt />
              </ProtectedRoute>
            } />
            
            <Route path="/student/stats" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentStats />
              </ProtectedRoute>
            } />
            <Route path="/student/messages" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentMessages />
              </ProtectedRoute>
            } />
            <Route path="/student/profile" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentProfile />
              </ProtectedRoute>
            } />
            <Route path="/student/class" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentMyClass />
              </ProtectedRoute>
            } />
            <Route path="/student/payments" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentPayments />
              </ProtectedRoute>
            } />

            {/* Professor Routes */}
            <Route path="/professor" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <Navigate to="/professor/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/professor/dashboard" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseList />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:id" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseView />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/create" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseCreator />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:id/edit" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseCreator />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:courseId/section/:sectionId/module/:moduleId" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseViewerPage />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:courseId/section/:sectionId/module/:moduleId/edit" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseViewerEdit />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:id/assign" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseAssignment />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizList />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/:id" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizView />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/create" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizCreator />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/:id/edit" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizEdit />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/:id/preview" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizPreview />
              </ProtectedRoute>
            } />
            <Route path="/professor/messages" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorMessages />
              </ProtectedRoute>
            } />
            <Route path="/professor/profile" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorProfile />
              </ProtectedRoute>
            } />
            <Route path="/professor/classes" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorClasses />
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Navigate to="/admin/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/admin/category" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCategory />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourses />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:id" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseView />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/creator" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseCreator />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:courseId/edit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseEdit />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:id/assign" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseAssignment />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:courseId/section/:sectionId/module/:moduleId" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseModuleViewer />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:courseId/section/:sectionId/module/:moduleId/edit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseModuleEdit />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminListQuiz />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/create" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizCreator />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/:id/creator" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizCreator />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/:id" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizView />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/:id/edit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizEdit />
              </ProtectedRoute>
            } />
             <Route path="/admin/quizzes/:id/preview" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizPreview />
              </ProtectedRoute>
            } />
             <Route path="/admin/quizzes/:quizId/admin-preview" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizPreview />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/review/:attemptId" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <QuizReview />
              </ProtectedRoute>
            } />
            <Route path="/admin/payments" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPayments />
              </ProtectedRoute>
            } />
            <Route path="/admin/stats" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminStats />
              </ProtectedRoute>
            } />
            <Route path="/admin/messages" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminMessages />
              </ProtectedRoute>
            } />
            <Route path="/admin/classes" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminClasses />
              </ProtectedRoute>
            } />

            {/* Sprint « traçabilité apprenants » (01/10/2026) */}
            <Route path="/admin/connections" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminConnections />
              </ProtectedRoute>
            } />
            <Route path="/admin/time-spent" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminTimeSpent />
              </ProtectedRoute>
            } />
            {/* Sprint « émargement » (09/10/2026) */}
            <Route path="/student/attendance" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentAttendance />
              </ProtectedRoute>
            } />
            <Route path="/professor/attendance" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorAttendance />
              </ProtectedRoute>
            } />
            <Route path="/admin/attendance" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminAttendance />
              </ProtectedRoute>
            } />
            {/* Route inconnue -> retour a la connexion (evite la page blanche sur une URL inexistante) */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </Router>
    
  );
}

export default App;